import {
  Box,
  Container,
  Stack,
  Link,
  SvgIcon,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Slider,
  IconButton,
  Chip,
  InputAdornment,
  MenuItem,
  Select,
  Divider,
  Tooltip,
  SelectChangeEvent,
} from "@mui/material";
import { Alert } from "@mui/material";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import ArrowLeftIcon from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  Replay10,
  Forward10,
  Search,
  Check,
  Close,
  Edit,
  Send,
} from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import {
  getFirestore,
  collection,
  onSnapshot,
  FirestoreError,
  doc,
  updateDoc,
} from "firebase/firestore";

// Declarações de tipos para YouTube API
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

export function EditarMidia() {
  const { id: roomId } = useParams();
  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const activeSentenceRef = useRef<HTMLDivElement>(null);

  // Video states
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [youtubePlayer, setYoutubePlayer] = useState<any>(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  // Transcription states
  const [transcription, setTranscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Layout states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpeaker, setSelectedSpeaker] = useState("All Speakers");

  // Inline editing states
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [facebookEmbedFailed, setFacebookEmbedFailed] = useState(false);

  const navigate = useNavigate();

  // Função para extrair ID do YouTube da URL
  const getYouTubeVideoId = (url: string) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Função para detectar a plataforma do vídeo
  const getVideoPlatform = (url: string) => {
    if (!url) return "unknown";

    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      return "youtube";
    } else if (url.includes("facebook.com") || url.includes("fb.watch")) {
      return "facebook";
    } else if (url.includes("vimeo.com")) {
      return "vimeo";
    } else if (url.includes("dailymotion.com")) {
      return "dailymotion";
    }

    return "unknown";
  };

  // Função para extrair ID do Facebook da URL
  const getFacebookVideoId = (url: string) => {
    // Padrão para URLs do Facebook: https://pt-br.facebook.com/100016812586029/videos/1509481410333660
    const regExp = /facebook\.com\/.*\/videos\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  // Carregar YouTube API
  useEffect(() => {
    const loadYouTubeAPI = () => {
      if (window.YT && window.YT.Player) {
        return;
      }

      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      document.head.appendChild(script);

      window.onYouTubeIframeAPIReady = () => {
        console.log("YouTube API carregada");
      };
    };

    loadYouTubeAPI();
  }, []);

  // Inicializar player do YouTube
  const initializeYouTubePlayer = (videoId: string) => {
    if (!window.YT || !window.YT.Player) {
      console.error("YouTube API não carregada");
      return;
    }

    const player = new window.YT.Player("youtube-player", {
      videoId: videoId,
      playerVars: {
        autoplay: 0,
        controls: 1,
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        fs: 1,
        cc_load_policy: 0,
        iv_load_policy: 3,
        autohide: 0,
      },
      events: {
        onReady: (event: any) => {
          console.log("Player pronto");
          setYoutubePlayer(event.target);
          setIsPlayerReady(true);
        },
        onStateChange: (event: any) => {
          // Esta função será chamada quando o estado do player mudar
          console.log("Estado do player mudou:", event.data);
        },
      },
    });
  };

  // Função para pular para um tempo específico
  const seekToTime = (timeInSeconds: number) => {
    if (youtubePlayer && youtubePlayer.seekTo) {
      youtubePlayer.seekTo(timeInSeconds, true);
    }
  };

  useEffect(() => {
    if (!roomId) return;

    const db = getFirestore();
    const transcriptionsRef = collection(db, "transcriptions");

    const unsubscribe = onSnapshot(
      transcriptionsRef,
      (querySnapshot) => {
        try {
          // Buscar documentos onde o campo 'sessao' corresponde ao roomId
          const matchingDocs = querySnapshot.docs.filter((doc) => {
            const data = doc.data();
            return data.sessao === roomId;
          });

          if (matchingDocs.length > 0) {
            // Pegar o documento mais recente (último criado)
            const latestDoc = matchingDocs[matchingDocs.length - 1];
            const data = latestDoc.data();
            setTranscription({ id: latestDoc.id, ...data });
          } else {
            setTranscription(null);
          }
          setLoading(false);
        } catch (err) {
          setError("Failed to parse transcription data");
          setLoading(false);
        }
      },
      (err: FirestoreError) => {
        setError(`Firestore error: ${err.message}`);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [roomId]);

  // Inicializar player quando transcrição for carregada
  useEffect(() => {
    if (transcription?.url && window.YT && window.YT.Player) {
      const videoId = getYouTubeVideoId(transcription.url);
      if (videoId) {
        // Aguardar um pouco para garantir que o DOM esteja pronto
        setTimeout(() => {
          initializeYouTubePlayer(videoId);
        }, 500);
      }
    }
  }, [transcription?.url]);

  // Atualizar tempo atual periodicamente quando o player estiver pronto
  useEffect(() => {
    if (!isPlayerReady || !youtubePlayer) return;

    const interval = setInterval(() => {
      if (youtubePlayer.getCurrentTime) {
        const currentTimeSeconds = youtubePlayer.getCurrentTime();
        setCurrentTime(currentTimeSeconds * 1000);
        scrollToActiveSentence();
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isPlayerReady, youtubePlayer, currentTime]);

  // useEffect removido - atalhos de teclado não são mais necessários com iframe do YouTube

  const scrollToActiveSentence = () => {
    if (transcriptContainerRef.current && transcription?.sentences) {
      const container = transcriptContainerRef.current;

      // Encontrar a frase ativa baseada no tempo atual
      const activeSentence = transcription.sentences.find(
        (sentence: any) =>
          currentTime >= sentence.start && currentTime <= sentence.end
      );

      if (activeSentence) {
        // Encontrar o elemento da frase ativa
        const sentenceElements = container.querySelectorAll(
          "[data-sentence-index]"
        );
        const activeElement = Array.from(sentenceElements).find(
          (el: any) =>
            parseInt(el.dataset.sentenceIndex) ===
            transcription.sentences.indexOf(activeSentence)
        );

        if (activeElement) {
          const containerHeight = container.clientHeight;
          const elementOffset =
            (activeElement as HTMLElement).offsetTop - container.offsetTop;
          const elementHeight = (activeElement as HTMLElement).clientHeight;

          const scrollPosition =
            elementOffset - containerHeight / 2 + elementHeight / 2;

          container.scrollTo({
            top: scrollPosition,
            behavior: "smooth",
          });
        }
      }
    }
  };

  // Função para sincronizar com o vídeo do YouTube (quando necessário)
  const handleTimeUpdate = () => {
    // Esta função pode ser usada para sincronizar com o iframe do YouTube
    // se necessário no futuro
  };

  const handleSpeakerChange = (event: SelectChangeEvent) => {
    setSelectedSpeaker(event.target.value as string);
  };

  const handleSaveEdit = async (index: number, newText: string) => {
    if (!transcription || !transcription.id) {
      setSaveError("Erro: Transcrição não encontrada");
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      // Atualizar as frases localmente
      const updatedSentences = [...transcription.sentences];
      updatedSentences[index].text = newText;

      // Reconstruir o texto completo a partir das frases atualizadas
      const updatedText = updatedSentences
        .map((sentence: any) => sentence.text)
        .join(" ");

      // Atualizar o estado local
      setTranscription({
        ...transcription,
        sentences: updatedSentences,
        text: updatedText,
      });

      // Salvar no Firebase
      const db = getFirestore();
      const transcriptionRef = doc(db, "transcriptions", transcription.id);

      await updateDoc(transcriptionRef, {
        sentences: updatedSentences,
        text: updatedText,
        updatedAt: new Date(),
      });

      console.log("Transcrição atualizada com sucesso");
      setEditingIndex(null);
    } catch (error) {
      console.error("Erro ao salvar transcrição:", error);
      setSaveError("Erro ao salvar alterações. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  // Função para salvar o texto completo (útil para grandes alterações)
  const handleSaveFullText = async () => {
    if (!transcription || !transcription.id) {
      setSaveError("Erro: Transcrição não encontrada");
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      // Reconstruir o texto completo a partir das frases atuais
      const updatedText = transcription.sentences
        .map((sentence: any) => sentence.text)
        .join(" ");

      // Salvar no Firebase
      const db = getFirestore();
      const transcriptionRef = doc(db, "transcriptions", transcription.id);

      await updateDoc(transcriptionRef, {
        text: updatedText,
        updatedAt: new Date(),
      });

      // Atualizar o estado local
      setTranscription({
        ...transcription,
        text: updatedText,
      });

      console.log("Texto completo atualizado com sucesso");
    } catch (error) {
      console.error("Erro ao salvar texto completo:", error);
      setSaveError("Erro ao salvar texto completo. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const formatTimeWithMilliseconds = (time: number) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Funções removidas - não são mais necessárias com iframe do YouTube

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!transcription) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <Typography>Não há dados de transcrição disponíveis</Typography>
      </Box>
    );
  }

  const filteredSentences = transcription.sentences?.filter((sentence: any) => {
    const matchesSearch = sentence.text
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesSpeaker =
      selectedSpeaker === "All Speakers" ||
      sentence.speaker === selectedSpeaker;
    return matchesSearch && matchesSpeaker;
  });

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
      <Container maxWidth="xl">
        <Stack spacing={4}>
          <div>
            <NavLink to="/sessoes">
              <Link
                color="text.primary"
                sx={{ alignItems: "center", display: "inline-flex" }}
                underline="hover"
              >
                <SvgIcon sx={{ mr: 1 }}>
                  <ArrowLeftIcon />
                </SvgIcon>
                <Typography variant="subtitle2">Voltar</Typography>
              </Link>
            </NavLink>
          </div>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h4">Transcrição</Typography>

            <TextField
              placeholder="Pesquisar transcrição..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
          </Box>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Video Section */}
            <div className="w-full lg:w-1/2">
              <div className="relative w-full">
                {transcription?.url ? (
                  (() => {
                    const platform = getVideoPlatform(transcription.url);
                    const youtubeId = getYouTubeVideoId(transcription.url);
                    const facebookId = getFacebookVideoId(transcription.url);

                    if (platform === "youtube" && youtubeId) {
                      return (
                        <div
                          className="relative w-full rounded-lg overflow-hidden shadow-lg"
                          style={{ paddingBottom: "56.25%" }}
                        >
                          <div
                            id="youtube-player"
                            className="absolute top-0 left-0 w-full h-full"
                          />
                          {!isPlayerReady && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                              <div className="text-center text-white">
                                <CircularProgress
                                  size={40}
                                  sx={{ color: "white", mb: 2 }}
                                />
                                <Typography variant="body2">
                                  Carregando player do YouTube...
                                </Typography>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    } else if (platform === "facebook" && facebookId) {
                      // Se o embed falhou, mostrar alternativa
                      if (facebookEmbedFailed) {
                        return (
                          <div className="w-full h-64 bg-gray-900 rounded-lg shadow-lg flex items-center justify-center">
                            <div className="text-center text-white p-6">
                              <svg
                                className="w-16 h-16 mx-auto mb-4 text-blue-500"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                              </svg>
                              <Typography variant="h6" gutterBottom>
                                Vídeo do Facebook
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 3 }}>
                                Este vídeo não pode ser incorporado devido às
                                configurações de privacidade
                              </Typography>
                              <Button
                                variant="contained"
                                href={transcription.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  mt: 1,
                                  backgroundColor: "#1877f2",
                                  "&:hover": {
                                    backgroundColor: "#166fe5",
                                  },
                                }}
                              >
                                Assistir no Facebook
                              </Button>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          className="relative w-full rounded-lg overflow-hidden shadow-lg"
                          style={{ paddingBottom: "56.25%" }}
                        >
                          <iframe
                            className="absolute top-0 left-0 w-full h-full"
                            src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
                              transcription.url
                            )}&show_text=false&width=560`}
                            title="Vídeo do Facebook"
                            frameBorder="0"
                            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                            allowFullScreen
                            onLoad={() => {
                              // Verificar se o iframe carregou com sucesso
                              setTimeout(() => {
                                const iframe = document.querySelector(
                                  'iframe[title="Vídeo do Facebook"]'
                                ) as HTMLIFrameElement;
                                if (
                                  iframe &&
                                  iframe.contentDocument?.body?.textContent?.includes(
                                    "Indisponível"
                                  )
                                ) {
                                  setFacebookEmbedFailed(true);
                                }
                              }, 2000);
                            }}
                          />
                          {/* Overlay com link direto para o Facebook */}
                          <div className="absolute inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                            <div className="text-center text-white p-6">
                              <svg
                                className="w-16 h-16 mx-auto mb-4 text-blue-500"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                              </svg>
                              <Typography variant="h6" gutterBottom>
                                Vídeo do Facebook
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 3 }}>
                                Clique para assistir diretamente no Facebook
                              </Typography>
                              <Button
                                variant="contained"
                                href={transcription.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  mt: 1,
                                  backgroundColor: "#1877f2",
                                  "&:hover": {
                                    backgroundColor: "#166fe5",
                                  },
                                }}
                              >
                                Assistir no Facebook
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    } else if (platform === "vimeo") {
                      // Extrair ID do Vimeo
                      const vimeoId =
                        transcription.url.match(/vimeo\.com\/(\d+)/)?.[1];
                      if (vimeoId) {
                        return (
                          <div
                            className="relative w-full rounded-lg overflow-hidden shadow-lg"
                            style={{ paddingBottom: "56.25%" }}
                          >
                            <iframe
                              className="absolute top-0 left-0 w-full h-full"
                              src={`https://player.vimeo.com/video/${vimeoId}`}
                              title="Vídeo do Vimeo"
                              frameBorder="0"
                              allow="autoplay; fullscreen; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        );
                      }
                    } else if (platform === "dailymotion") {
                      // Extrair ID do Dailymotion
                      const dailymotionId = transcription.url.match(
                        /dailymotion\.com\/video\/([^_]+)/
                      )?.[1];
                      if (dailymotionId) {
                        return (
                          <div
                            className="relative w-full rounded-lg overflow-hidden shadow-lg"
                            style={{ paddingBottom: "56.25%" }}
                          >
                            <iframe
                              className="absolute top-0 left-0 w-full h-full"
                              src={`https://www.dailymotion.com/embed/video/${dailymotionId}`}
                              title="Vídeo do Dailymotion"
                              frameBorder="0"
                              allow="autoplay; fullscreen; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        );
                      }
                    }

                    // Se chegou aqui, a plataforma não é suportada ou a URL é inválida
                    return (
                      <div className="w-full h-64 bg-gray-900 rounded-lg shadow-lg flex items-center justify-center">
                        <div className="text-center text-white">
                          <Typography variant="h6" gutterBottom>
                            Vídeo não disponível
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {platform === "unknown"
                              ? "Plataforma não suportada"
                              : "URL do vídeo inválida"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            Plataforma detectada: {platform}
                          </Typography>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="w-full h-64 bg-gray-900 rounded-lg shadow-lg flex items-center justify-center">
                    <div className="text-center text-white">
                      <Typography variant="h6" gutterBottom>
                        Vídeo não disponível
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum vídeo encontrado
                      </Typography>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex mt-5 items-start flex-col justify-between mb-4">
                <Typography variant="h6" gutterBottom>
                  Vídeo da Sessão
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  ID do Vídeo: {transcription?.video_id || "N/A"}
                </Typography>
              </div>
            </div>

            {/* Transcription Section */}
            <div className="w-full lg:w-1/2 flex flex-col">
              {/* Status de salvamento */}
              {saving && (
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="primary">
                      Salvando alterações...
                    </Typography>
                  </div>
                </div>
              )}

              <div
                ref={transcriptContainerRef}
                className="flex-1 overflow-y-auto bg-white rounded-lg shadow-lg p-4"
                style={{ maxHeight: "calc(100vh - 300px)" }}
              >
                <Box
                  sx={{
                    backgroundColor: "background.paper",
                    borderRadius: 1,
                    boxShadow: 1,
                    p: 3,
                  }}
                >
                  {filteredSentences?.map((sentence: any, index: number) => {
                    const isActive =
                      currentTime >= sentence.start &&
                      currentTime <= sentence.end;

                    return (
                      <Box
                        key={index}
                        data-sentence-index={index}
                        ref={isActive ? activeSentenceRef : null}
                        sx={{
                          mb: 3,
                          backgroundColor: isActive
                            ? "action.selected"
                            : "transparent",
                          borderRadius: 1,
                          p: 2,
                          transition: "background-color 0.2s",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: isActive
                              ? "action.selected"
                              : "action.hover",
                          },
                        }}
                      >
                        {editingIndex === index ? (
                          <div className="flex flex-col space-y-2">
                            <TextField
                              fullWidth
                              multiline
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              autoFocus
                              disabled={saving}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                },
                              }}
                            />
                            {saveError && (
                              <Alert
                                severity="error"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                {saveError}
                              </Alert>
                            )}
                            <div className="flex space-x-2">
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleSaveEdit(index, editText)}
                                disabled={saving}
                                startIcon={
                                  saving ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Check fontSize="small" />
                                  )
                                }
                              >
                                {saving ? "Salvando..." : "Salvar alterações"}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setEditingIndex(null);
                                  setSaveError(null);
                                }}
                                disabled={saving}
                              >
                                <Close fontSize="small" sx={{ mr: 1 }} />
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => {
                              // Pular para o tempo da frase no vídeo
                              const timeInSeconds = sentence.start / 1000;
                              seekToTime(timeInSeconds);
                            }}
                          >
                            <div className="flex items-center mb-1">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatTimeWithMilliseconds(
                                  sentence.start / 1000
                                )}
                              </Typography>
                              <IconButton
                                size="small"
                                sx={{ ml: "auto" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingIndex(index);
                                  setEditText(sentence.text);
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </div>
                            <Typography>{sentence.text}</Typography>
                          </div>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex space-x-3">
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() =>
                      navigate(`/sessoes/${roomId}/resumo/midia/confirmar-ata`)
                    }
                    disabled={saving}
                  >
                    Visualizar tudo e confirmar ata
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    size="large"
                    onClick={handleSaveFullText}
                    disabled={saving}
                    startIcon={
                      saving ? <CircularProgress size={16} /> : <Send />
                    }
                  >
                    {saving ? "Salvando..." : "Salvar todas as alterações"}
                  </Button>
                </div>

                {saveError && (
                  <Alert severity="error" onClose={() => setSaveError(null)}>
                    {saveError}
                  </Alert>
                )}

                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>Entrou algum erro?</strong> Entre em contato com a
                  equipe de suporte. Podemos ajudar você a corrigir.
                </Typography>
              </div>
            </div>
          </div>
        </Stack>
      </Container>
    </Box>
  );
}
